import React, { useRef, type ReactNode } from "react";
import { Box } from "@mui/material";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform
} from "framer-motion";

type WelcomeParallaxProps = {
  children: ReactNode;
  offset?: number;
  /** Swap scroll direction for counter-motion layers (e.g. text vs image). */
  direction?: "normal" | "reverse";
};

/** Subtle vertical parallax for decorative layers inside welcome sections. */
const WelcomeParallax: React.FC<WelcomeParallaxProps> = ({
  children,
  offset = 56,
  direction = "normal"
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const start = direction === "reverse" ? -offset : offset;
  const end = direction === "reverse" ? offset : -offset;
  const y = useTransform(scrollYProgress, [0, 1], [start, end]);

  return (
    <Box ref={ref} sx={{ width: "100%", overflow: "visible" }}>
      <motion.div
        style={{
          y: prefersReducedMotion ? 0 : y,
          position: "relative",
          willChange: prefersReducedMotion ? undefined : "transform"
        }}
      >
        {children}
      </motion.div>
    </Box>
  );
};

export default WelcomeParallax;
